import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from './api';
import { keys } from '@/lib/query/keys';
import { useApiError } from '@/lib/query/useApiError';
import { DataView } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Button } from '@/lib/ui/Button';
import { Dialog } from '@/lib/ui/Dialog';
import { TextInput } from '@/lib/ui/Field';
import { MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { I18nField } from '@/lib/forms/widgets/I18nField';
import { useToast } from '@/lib/ui';
import type { I18n, UiString } from '@/lib/api/types';

const EMPTY: I18n = { en: '', ar: '' };

/** UI strings tab — manage namespaced bilingual strings (spec §15.2). */
export function StringsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();

  const query = useQuery({
    queryKey: keys.settings.strings(),
    queryFn: () => settingsApi.listStrings(),
  });

  const [editing, setEditing] = useState<UiString | null>(null);
  const [ns, setNs] = useState('');
  const [key, setKey] = useState('');
  const [value, setValue] = useState<I18n>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const openEdit = (row: UiString) => {
    setEditing(row);
    setNs(row.namespace);
    setKey(row.key);
    setValue(row.value);
    setErrors({});
  };
  const close = () => {
    setEditing(null);
  };

  const invalidate = () => qc.invalidateQueries({ queryKey: keys.settings.strings() });

  const save = useMutation({
    mutationFn: () => settingsApi.putString(ns.trim(), key.trim(), value),
    onSuccess: () => {
      void invalidate();
      toast.success(t('settings.strings.savedTitle'), t('settings.strings.savedDetail'));
      close();
    },
    onError: (e) => onError(e, { onValidation: setErrors }),
  });

  const submit = () => {
    const next: Record<string, string> = {};
    if (!ns.trim()) next.namespace = t('settings.strings.errNamespaceRequired');
    if (!key.trim()) next.key = t('settings.strings.errKeyRequired');
    if (!value.en.trim()) next['value.en'] = t('settings.strings.errValueRequired');
    setErrors(next);
    if (Object.keys(next).length) return;
    save.mutate(undefined);
  };

  const columns: Column<UiString>[] = useMemo(
    () => [
      { id: 'ns', header: t('settings.strings.colNamespace'), cell: (r) => <MonoValue>{r.namespace}</MonoValue> },
      { id: 'key', header: t('common.key'), cell: (r) => <MonoValue>{r.key}</MonoValue> },
      {
        id: 'en',
        header: t('common.en'),
        cell: (r) => <span className="text-ink-deep">{r.value.en || '—'}</span>,
      },
      {
        id: 'ar',
        header: t('common.ar'),
        cell: (r) =>
          r.value.ar.trim() ? (
            <span dir="rtl" className="font-arabic text-ink-deep">
              {r.value.ar}
            </span>
          ) : (
            <Tag variant="amber">{t('settings.strings.missingAr')}</Tag>
          ),
      },
      {
        id: 'actions',
        header: '',
        align: 'end',
        cell: (r) => (
          <div className="flex justify-end gap-xs" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>
              {t('common.edit')}
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <div>
      <DataView query={query} isEmpty={(d) => d.length === 0}>
        {(rows) => (
          <DataTable columns={columns} rows={rows} rowKey={(r) => `${r.namespace}/${r.key}`} />
        )}
      </DataView>

      <Dialog
        open={!!editing}
        onOpenChange={(o) => !o && close()}
        title={t('settings.strings.editString')}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button loading={save.isPending} onClick={submit}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-md">
          <TextInput
            label={t('settings.strings.namespace')}
            required
            value={ns}
            disabled
            onChange={(e) => setNs(e.target.value)}
            error={errors.namespace}
          />
          <TextInput
            label={t('common.key')}
            required
            value={key}
            disabled
            onChange={(e) => setKey(e.target.value)}
            error={errors.key}
          />
          <I18nField
            label={t('settings.strings.value')}
            value={value}
            onChange={setValue}
            required
            error={errors['value.en']}
          />
        </div>
      </Dialog>
    </div>
  );
}
